<template>
  <div class="flex h-full w-full justify-center p-3 md:p-5">
    <div class="flex w-full max-w-3xl max-h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-md shadow-slate-900/10">
      <header class="flex items-start justify-between gap-3 bg-gradient-to-br from-blue-500/15 to-blue-500/5 px-4 py-3">
        <div class="flex min-w-0 flex-1 flex-col gap-2">
          <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <p class="text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-600">Aperçu du sondage</p>
            <div v-if="contentUuid || canExportStats" class="inline-flex flex-wrap items-center justify-end gap-2">
              <button
                v-if="canExportStats"
                type="button"
                class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-blue-600 px-2.5 py-1 text-[11px] font-semibold leading-none text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="isExportingStats"
                @click="handleStatsExport"
              >
                <i
                  :class="isExportingStats ? 'fas fa-spinner fa-spin' : 'fas fa-file-export'"
                  aria-hidden="true"
                ></i>
                <span>{{ isExportingStats ? 'Export en cours…' : 'Exporter les statistiques' }}</span>
              </button>
              <button
                v-if="contentUuid"
                type="button"
                class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-blue-500/20 bg-blue-500/10 px-2 py-1 text-[11px] font-semibold leading-none text-blue-700 transition hover:border-blue-500/50 hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                :class="{
                  'border-green-500/30 bg-green-500/10 text-green-700': uuidCopyStatus === 'copied',
                  'border-red-500/30 bg-red-500/10 text-red-700': uuidCopyStatus === 'error'
                }"
                :title="`Copier l'UUID complet : ${contentUuid}`"
                :aria-label="`Copier l'UUID complet ${contentUuid}`"
                @click="copyContentUuid"
              >
                <span>UUID: {{ truncatedUuid }}</span>
                <i
                  class="fas"
                  :class="uuidCopyStatus === 'copied' ? 'fa-check' : (uuidCopyStatus === 'error' ? 'fa-exclamation-circle' : 'fa-copy')"
                  aria-hidden="true"
                ></i>
              </button>
              <span
                class="text-[11px] font-semibold"
                :class="uuidCopyStatus === 'error' ? 'text-red-700' : 'text-green-700'"
                role="status"
                aria-live="polite"
              >{{ uuidCopyMessage }}</span>
            </div>
          </div>
          <h3 class="text-lg font-semibold leading-snug text-slate-900">{{ displayTitle }}</h3>
        </div>
        <button
          type="button"
          class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-500/10 text-blue-600 transition hover:-translate-y-0.5 hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          @click="close"
          aria-label="Fermer l'aperçu"
        >
          <i class="fas fa-times text-sm"></i>
        </button>
      </header>

      <div class="flex flex-1 overflow-y-auto px-4 py-5">
        <div v-if="loading" class="mx-auto flex flex-col items-center gap-2 text-xs text-slate-500">
          <i class="fas fa-spinner fa-spin text-blue-500"></i>
          <span>Chargement de l'aperçu…</span>
        </div>

        <div v-else-if="error" class="mx-auto flex max-w-sm flex-col items-center gap-1.5 text-center text-xs text-red-600">
          <i class="fas fa-triangle-exclamation"></i>
          <span>{{ error }}</span>
        </div>

        <div v-else-if="previewGroup" class="flex w-full flex-col gap-3.5">
          <ul class="flex flex-col gap-1 text-xs text-slate-600">
            <li v-if="scheduleLabel" class="inline-flex items-center gap-1.5">
              <i class="fas fa-calendar-alt text-blue-500"></i>
              <span>{{ scheduleLabel }}</span>
            </li>
            <li v-if="groupLabels.length" class="inline-flex items-center gap-1.5">
              <i class="fas fa-users text-blue-500"></i>
              <span>{{ associatedUserGroupsLabel }}</span>
            </li>
          </ul>

          <div v-if="badgeLabels.length" class="flex flex-wrap gap-1">
            <span
              v-for="label in badgeLabels"
              :key="label"
              class="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-[11px] font-medium text-blue-700"
            >
              {{ label }}
            </span>
          </div>

          <p v-if="previewGroup.description" class="text-sm leading-relaxed text-slate-700">
            {{ previewGroup.description }}
          </p>

          <div v-if="questionList.length" class="flex flex-col gap-3">
            <section
              v-for="(question, index) in questionList"
              :key="question.uuid || `question-${index}`"
              class="flex flex-col gap-2.5 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm shadow-slate-900/5"
            >
              <div>
                <span class="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-500">
                  Question {{ index + 1 }}
                </span>
                <p class="mt-1 text-base font-semibold text-slate-900">
                  {{ question.question }}
                </p>
              </div>

              <p v-if="question.description" class="text-sm leading-relaxed text-slate-600">
                {{ question.description }}
              </p>

              <div v-if="questionBadges(question).length" class="flex flex-wrap gap-1">
                <span
                  v-for="badge in questionBadges(question)"
                  :key="badge"
                  class="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                >
                  {{ badge }}
                </span>
              </div>

              <div v-if="question.options.length" class="rounded-md bg-blue-500/5 px-3 py-2">
                <h4 class="mb-1 text-xs font-semibold text-blue-700">Réponses proposées</h4>
                <ol class="list-decimal space-y-1 pl-5 text-sm text-slate-700">
                  <li
                    v-for="(option, optionIndex) in question.options"
                    :key="option.uuid || `option-${optionIndex}`"
                  >
                    {{ option.label }}
                  </li>
                </ol>
              </div>

              <div v-else class="text-sm text-slate-500">
                Aucune réponse n'est définie pour cette question.
              </div>
            </section>
          </div>

          <div v-else class="text-sm text-slate-500">
            Aucune question n'est disponible pour ce sondage.
          </div>
        </div>

        <div v-else class="mx-auto flex flex-col items-center gap-2 text-xs text-slate-500">
          <span>Aucune donnée à afficher.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { http } from '@/services/api.service'
import { resolveAssociatedUserGroupLabels } from '@/utils/associatedUserGroups'

const ensureArray = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean)
  }
  if (!value) {
    return []
  }
  if (typeof value === 'object') {
    return Object.values(value).filter(Boolean)
  }
  return [value]
}

const parseDateSafe = (value) => {
  if (!value) return null
  try {
    return typeof value === 'string' ? parseISO(value) : value
  } catch (_) {
    try {
      const normalized = typeof value === 'string' ? value.replace(' ', 'T') : value
      const fallback = new Date(normalized)
      return Number.isNaN(fallback.getTime()) ? null : fallback
    } catch (e) {
      return null
    }
  }
}

const selectBoundaryDate = (values, mode) => {
  const timestamps = ensureArray(values)
    .map(parseDateSafe)
    .filter(Boolean)
    .map((date) => date.getTime())

  if (!timestamps.length) {
    return null
  }

  const time = mode === 'max' ? Math.max(...timestamps) : Math.min(...timestamps)
  return new Date(time).toISOString()
}

const coerceBoolean = (value) => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true'
  return false
}

const mapOptions = (options, ownerUuid) => ensureArray(options).map((option, index) => {
  const label = option?.option || option?.label || option?.title
  if (!label) {
    return null
  }
  return {
    uuid: option.uuid || `${ownerUuid || 'option'}-${index}`,
    label
  }
}).filter(Boolean)

const hasCustomAnswer = (options) => ensureArray(options).some((option) => {
  const label = typeof option?.option === 'string' ? option.option.toLowerCase() : ''
  return label.includes('autre')
})

export default {
  name: 'SurveyPreviewModal',
  props: {
    surveyUuid: {
      type: String,
      required: true
    },
    surveyGroupUuid: {
      type: String,
      default: null
    },
    initialTitle: {
      type: String,
      default: ''
    },
    source: {
      type: String,
      default: 'pending'
    },
    canExportStats: {
      type: Boolean,
      default: false
    },
    exportStats: {
      type: Function,
      default: null
    }
  },
  data () {
    return {
      loading: false,
      error: null,
      previewGroup: null,
      requestToken: 0,
      previewCache: {},
      listCache: {
        domain: null,
        maviepro: null
      },
      isExportingStats: false,
      uuidCopyStatus: 'idle',
      uuidCopyTimer: null
    }
  },
  computed: {
    applicationSlug () {
      const application = this.$store.getters.application
      return application?.app?.slug || null
    },
    displayTitle () {
      return this.previewGroup?.title || this.initialTitle || 'Sondage'
    },
    contentUuid () {
      const uuid = this.previewGroup?.uuid || this.surveyGroupUuid || this.surveyUuid
      return uuid ? String(uuid) : ''
    },
    truncatedUuid () {
      return this.contentUuid.length > 16
        ? `${this.contentUuid.slice(0, 14)}…`
        : this.contentUuid
    },
    uuidCopyMessage () {
      if (this.uuidCopyStatus === 'copied') {
        return 'Copié'
      }
      if (this.uuidCopyStatus === 'error') {
        return 'Copie impossible'
      }
      return ''
    },
    groupLabels () {
      return ensureArray(this.previewGroup?.groups)
    },
    associatedUserGroupsLabel () {
      const prefix = this.groupLabels.length > 1 ? 'Groupes utilisateurs associés' : 'Groupe utilisateur associé'
      return `${prefix} : ${this.groupLabels.join(', ')}`
    },
    badgeLabels () {
      const badges = new Set()
      const questions = this.questionList
      if (questions.some((question) => question.multiple_answers)) badges.add('Plusieurs réponses possibles')
      if (questions.some((question) => question.allows_custom_answer)) badges.add('Réponse personnalisée autorisée')
      if (questions.some((question) => question.is_reanswerable)) badges.add('Réponse modifiable')
      if (this.previewGroup?.is_notified || questions.some((question) => question.is_notified)) badges.add('Notification activée')
      return Array.from(badges)
    },
    scheduleLabel () {
      const startDate = parseDateSafe(this.previewGroup?.start_at || this.previewGroup?.publication_date)
      const endDate = parseDateSafe(this.previewGroup?.end_at)
      if (startDate && endDate) {
        return `Disponible du ${format(startDate, 'dd/MM/yyyy HH:mm')} au ${format(endDate, 'dd/MM/yyyy HH:mm')}`
      }
      if (startDate) {
        return `Débute le ${format(startDate, 'dd/MM/yyyy HH:mm')}`
      }
      if (endDate) {
        return `Expire le ${format(endDate, 'dd/MM/yyyy HH:mm')}`
      }
      return null
    },
    questionList () {
      return ensureArray(this.previewGroup?.surveys)
    }
  },
  watch: {
    surveyUuid: 'fetchSurvey',
    surveyGroupUuid: 'fetchSurvey',
    source: 'fetchSurvey',
    contentUuid () {
      this.resetUuidCopyStatus()
    }
  },
  created () {
    this.fetchSurvey()
  },
  beforeDestroy () {
    this.clearUuidCopyTimer()
  },
  methods: {
    async handleStatsExport () {
      if (!this.canExportStats || !this.exportStats || this.isExportingStats) {
        return
      }

      this.isExportingStats = true
      try {
        await this.exportStats()
      } finally {
        this.isExportingStats = false
      }
    },
    isGroupVisibleForApplication (group) {
      const slug = this.applicationSlug
      if (!slug || !['maviepro', 'tract'].includes(slug)) {
        return true
      }
      const visibility = (group && group.media_mvp_visibility) || 'both'
      if (!visibility || visibility === 'both') {
        return true
      }
      return visibility === slug
    },
    filterGroupsByApplication (groups) {
      if (!Array.isArray(groups)) {
        return []
      }
      return groups.filter((group) => this.isGroupVisibleForApplication(group))
    },
    async fetchSurvey () {
      const token = ++this.requestToken
      const identifiers = Array.from(new Set([this.surveyGroupUuid, this.surveyUuid].filter(Boolean)))

      if (!identifiers.length) {
        this.previewGroup = null
        this.error = 'Identifiant du sondage manquant.'
        return
      }

      this.loading = true
      this.error = null

      try {
        let preview = this.lookupCache(identifiers)
        if (!preview) {
          preview = this.source === 'maviepro'
            ? await this.resolveMavieproPreview(identifiers)
            : await this.resolveDomainPreview(identifiers)
        }

        if (this.requestToken !== token) {
          return
        }

        if (preview) {
          this.previewGroup = preview
        } else {
          this.previewGroup = null
          this.error = 'Aperçu indisponible pour ce sondage.'
        }
      } catch (error) {
        if (this.requestToken === token) {
          this.previewGroup = null
          this.error = this.source === 'maviepro'
            ? 'Impossible de récupérer ce sondage Maviepro pour le moment.'
            : 'Impossible de charger ce sondage pour le moment.'
        }
      } finally {
        if (this.requestToken === token) {
          this.loading = false
        }
      }
    },
    lookupCache (identifiers) {
      for (const identifier of identifiers) {
        const cached = this.previewCache[String(identifier)]
        if (cached && this.isGroupVisibleForApplication(cached)) {
          return cached
        }
      }
      return null
    },
    async resolveDomainPreview (identifiers) {
      await this.ensureDomainList()
      let preview = this.lookupCache(identifiers)
      if (preview) {
        return preview
      }

      for (const identifier of identifiers) {
        preview = await this.fetchSingleSurvey(identifier)
        if (preview) {
          return preview
        }
      }
      return null
    },
    async resolveMavieproPreview (identifiers) {
      for (const identifier of identifiers) {
        const preview = await this.fetchMavieproDirect(identifier)
        if (preview) {
          return preview
        }
      }
      await this.ensureMavieproList()
      return this.lookupCache(identifiers)
    },
    async ensureDomainList () {
      if (Array.isArray(this.listCache.domain)) {
        return
      }
      const response = await http.get('/domain/surveys', { params: { multiple: true, past: true } })
      const entries = Array.isArray(response?.data) ? response.data : []
      this.listCache.domain = entries
      entries.forEach((entry) => {
        const normalized = this.normalizeSurveyGroup(entry)
        if (normalized) {
          this.cachePreview(normalized)
        }
      })
    },
    async ensureMavieproList () {
      if (Array.isArray(this.listCache.maviepro)) {
        return
      }
      const response = await http.get('/surveys/surveys-mvp')
      const entries = Array.isArray(response?.data) ? response.data : []
      const filteredEntries = this.filterGroupsByApplication(entries)
      this.listCache.maviepro = filteredEntries
      filteredEntries.forEach((entry) => {
        const normalized = this.normalizeMavieproGroup(entry)
        if (normalized) {
          this.cachePreview(normalized)
        }
      })
    },
    async fetchMavieproDirect (uuid) {
      if (!uuid) return null
      const cached = this.lookupCache([uuid])
      if (cached) return cached
      try {
        const response = await http.get(`/surveys/surveys-mvp/${uuid}`)
        const normalized = this.normalizeMavieproGroup(response?.data)
        if (normalized && this.isGroupVisibleForApplication(normalized)) {
          this.cachePreview(normalized)
          return normalized
        }
        return null
      } catch (error) {
        return null
      }
    },
    async fetchSingleSurvey (uuid) {
      if (!uuid) return null
      const cached = this.lookupCache([uuid])
      if (cached) return cached
      const response = await http.get(`/surveys/${uuid}`).catch(() => null)
      const data = response?.data
      if (!data) {
        return null
      }
      const normalized = this.normalizeSurveyGroup({
        uuid: data.uuid,
        group_title: data.question,
        start_at: data.start_at,
        end_at: data.end_at,
        publication_date: data.send_notification_at || data.start_at,
        is_notified: data.is_notified,
        description: data.description,
        groups: data.groups,
        surveys: [data]
      })
      if (normalized) {
        this.cachePreview(normalized)
      }
      return normalized
    },
    normalizeMavieproGroup (entry) {
      if (!entry) return null
      const clone = { ...entry }
      clone.surveys = ensureArray(entry.surveys).map((survey) => ({ ...survey }))
      clone.description = entry.description || entry.group_description || ''
      clone.group_title = entry.group_title || clone.surveys[0]?.question || this.initialTitle || 'Sondage'
      return this.normalizeSurveyGroup(clone)
    },
    normalizeSurveyGroup (entry) {
      if (!entry) return null

      const sourceSurveys = ensureArray(entry.surveys)
      const surveys = sourceSurveys
        .map((survey) => this.normalizeSurvey(survey))
        .filter(Boolean)

      if (!surveys.length) {
        const single = this.normalizeSurvey(entry)
        if (single) {
          surveys.push(single)
        }
      }

      if (!surveys.length) {
        return null
      }

      const title = entry.group_title || entry.title || surveys[0].question || this.initialTitle || 'Sondage'
      const description = entry.description || surveys.find((survey) => survey.description)?.description || ''
      const startAt = entry.start_at || selectBoundaryDate(surveys.map((survey) => survey.start_at), 'min')
      const endAt = entry.end_at || selectBoundaryDate(surveys.map((survey) => survey.end_at), 'max')
      const publicationDate = entry.publication_date || entry.send_notification_at || startAt
      const groups = resolveAssociatedUserGroupLabels(
        entry.group_labels,
        entry.groups,
        ensureArray(entry.notifications).map(notification => notification?.group),
        sourceSurveys.flatMap(survey => ensureArray(survey?.groups)),
        sourceSurveys.flatMap(survey => ensureArray(survey?.notifications).map(notification => notification?.group))
      )

      return {
        uuid: entry.uuid || surveys[0].uuid || null,
        title,
        description,
        start_at: startAt,
        end_at: endAt,
        publication_date: publicationDate,
        is_notified: coerceBoolean(entry.is_notified) || surveys.some((survey) => survey.is_notified),
        groups,
        surveys,
        media_mvp_visibility: entry.media_mvp_visibility || 'both'
      }
    },
    normalizeSurvey (survey) {
      if (!survey) return null
      const options = mapOptions(survey.options, survey.uuid)
      return {
        uuid: survey.uuid || null,
        question: survey.question || survey.title || survey.group_title || 'Question',
        description: survey.description || survey.content || '',
        allows_custom_answer: hasCustomAnswer(survey.options),
        multiple_answers: Boolean(survey.multiple_answers),
        is_reanswerable: Boolean(survey.is_reanswerable),
        is_notified: coerceBoolean(survey.is_notified),
        start_at: survey.start_at || survey.publication_date || null,
        end_at: survey.end_at || null,
        options
      }
    },
    questionBadges (question) {
      if (!question) return []
      const badges = []
      if (question.multiple_answers) badges.push('Plusieurs réponses possibles')
      if (question.allows_custom_answer) badges.push('Réponse personnalisée autorisée')
      if (question.is_reanswerable) badges.push('Réponse modifiable')
      return badges
    },
    cachePreview (preview) {
      if (!preview) return
      const store = (key) => {
        if (key) {
          this.$set(this.previewCache, String(key), preview)
        }
      }
      store(preview.uuid)
      ensureArray(preview.surveys).forEach((survey) => store(survey.uuid))
    },
    async copyContentUuid () {
      if (!this.contentUuid) {
        return
      }

      try {
        if (navigator.clipboard && window.isSecureContext) {
          try {
            await navigator.clipboard.writeText(this.contentUuid)
          } catch (error) {
            this.copyWithFallback(this.contentUuid)
          }
        } else {
          this.copyWithFallback(this.contentUuid)
        }
        this.setUuidCopyStatus('copied')
      } catch (error) {
        this.setUuidCopyStatus('error')
      }
    },
    copyWithFallback (value) {
      const textarea = document.createElement('textarea')
      textarea.value = value
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      let copied = false
      try {
        textarea.select()
        copied = document.execCommand('copy')
      } finally {
        document.body.removeChild(textarea)
      }
      if (!copied) {
        throw new Error('Clipboard copy failed')
      }
    },
    setUuidCopyStatus (status) {
      this.clearUuidCopyTimer()
      this.uuidCopyStatus = status
      this.uuidCopyTimer = window.setTimeout(() => {
        this.uuidCopyStatus = 'idle'
        this.uuidCopyTimer = null
      }, 2500)
    },
    resetUuidCopyStatus () {
      this.clearUuidCopyTimer()
      this.uuidCopyStatus = 'idle'
    },
    clearUuidCopyTimer () {
      if (this.uuidCopyTimer !== null) {
        window.clearTimeout(this.uuidCopyTimer)
        this.uuidCopyTimer = null
      }
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>
